/**
 * ProjectTeamTab — IN REVIEW stage
 * Expanded person cards with photo avatars, roles, add/remove
 */

import { useState } from 'react'
import type { ProjectDetailProject, ProjectResearcher } from '../../../types'
import { InlineNote } from '../../ui/InlineNote'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

const SPIN_OFF_INTENT_OPTIONS = [
  { value: 'Confirmed', label: 'Confirmed Interest' },
  { value: 'Likely', label: 'Likely' },
  { value: 'Uncertain', label: 'Uncertain' },
  { value: 'Not Interested', label: 'Not Interested' },
]

const AVAILABILITY_OPTIONS = [
  { value: 'Full-time', label: 'Full-time' },
  { value: 'Part-time', label: 'Part-time' },
  { value: 'Uncertain', label: 'Uncertain' },
  { value: 'Not available', label: 'Not available' },
]

const BIZ_EXPERIENCE_OPTIONS = [
  { value: 'Expert', label: 'Expert' },
  { value: 'Experienced', label: 'Experienced' },
  { value: 'Some', label: 'Some' },
  { value: 'None', label: 'None' },
]

export function ProjectTeamTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions: sc,
  tabId = 'tab-project-team',
}: TabContentProps) {
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId, sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId, sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId, sectionId)

  const teamMembers = project.team || []
  const [teamAssessments, setTeamAssessments] = useState<
    Record<number, { spinoffIntent: string; availability: string; bizExp: string }>
  >({})

  return (
    <div className="space-y-6">
      {/* Team Members List */}
      <CompletableSection
        sectionId="tm-members"
        title="Team Members"
        isComplete={isComplete('tm-members')}
        completedByName={getInfo('tm-members')?.completed_by_name}
        completedAt={getInfo('tm-members')?.completed_at}
        onToggle={() => toggle('tm-members')}
      >
        <div className="space-y-4">
        {teamMembers.length > 0 ? (
          teamMembers.map((member: ProjectResearcher, idx: number) => (
            <div
              key={idx}
              className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-5"
            >
              {/* Header with photo, name, role */}
              <div className="flex gap-4 mb-4">
                {/* Avatar */}
                <div
                  className="
                    w-12 h-12 rounded-full flex-shrink-0
                    bg-gradient-to-br from-[#8B5CF6] to-[#E61B25]
                    flex items-center justify-center
                    text-[13px] font-semibold text-white
                  "
                >
                  {member.researcher?.name?.charAt(0) || 'U'}
                </div>

                {/* Info */}
                <div className="flex-1">
                  <div className="text-[14px] font-semibold text-[#e8e8f0]">
                    {member.researcher?.name}
                  </div>
                  <div className="text-[12px] text-[#a8a8c0] mb-2">
                    {member.role}
                  </div>
                  <div className="flex gap-2">
                    <span className="
                      inline-block px-2 py-1
                      bg-[#12122a] border border-[#2a2a44]
                      rounded text-[11px] text-[#a8a8c0]
                    ">
                      {member.researcher?.area_of_expertise || 'Specialist'}
                    </span>
                    {member.role?.includes('Founder') && (
                      <span className="
                        inline-block px-2 py-1
                        bg-[rgba(230,27,37,0.15)] text-[#E61B25]
                        rounded text-[11px] font-medium
                      ">
                        Potential Founder
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* 3-Column Assessment Grid */}
              <div className="grid grid-cols-3 gap-3">
                {/* Spin-off Intent */}
                <div>
                  <label className="text-[11px] font-medium text-[#a8a8c0] block mb-2">
                    Spin-off Intent
                  </label>
                  <select
                    value={teamAssessments[idx]?.spinoffIntent || ''}
                    onChange={(e) => {
                      const updated = { ...teamAssessments[idx] }
                      updated.spinoffIntent = e.target.value
                      setTeamAssessments({ ...teamAssessments, [idx]: updated })
                    }}
                    className="
                      w-full px-2 py-1.5
                      bg-[#12122a] border border-[#2a2a44]
                      rounded text-[11px] text-[#e8e8f0]
                      focus:border-[#3a3a54]
                    "
                  >
                    <option value="">Select</option>
                    {SPIN_OFF_INTENT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Availability */}
                <div>
                  <label className="text-[11px] font-medium text-[#a8a8c0] block mb-2">
                    Availability
                  </label>
                  <select
                    value={teamAssessments[idx]?.availability || ''}
                    onChange={(e) => {
                      const updated = { ...teamAssessments[idx] }
                      updated.availability = e.target.value
                      setTeamAssessments({ ...teamAssessments, [idx]: updated })
                    }}
                    className="
                      w-full px-2 py-1.5
                      bg-[#12122a] border border-[#2a2a44]
                      rounded text-[11px] text-[#e8e8f0]
                      focus:border-[#3a3a54]
                    "
                  >
                    <option value="">Select</option>
                    {AVAILABILITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Business Experience */}
                <div>
                  <label className="text-[11px] font-medium text-[#a8a8c0] block mb-2">
                    Biz Experience
                  </label>
                  <select
                    value={teamAssessments[idx]?.bizExp || ''}
                    onChange={(e) => {
                      const updated = { ...teamAssessments[idx] }
                      updated.bizExp = e.target.value
                      setTeamAssessments({ ...teamAssessments, [idx]: updated })
                    }}
                    className="
                      w-full px-2 py-1.5
                      bg-[#12122a] border border-[#2a2a44]
                      rounded text-[11px] text-[#e8e8f0]
                      focus:border-[#3a3a54]
                    "
                  >
                    <option value="">Select</option>
                    {BIZ_EXPERIENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Remove button */}
              <button
                className="
                  mt-3 text-[12px] text-[#E61B25]
                  hover:text-[#d41620]
                  font-medium
                "
              >
                Remove from Team
              </button>
            </div>
          ))
        ) : (
          <div className="
            bg-[#1e1e38] border border-[#2a2a44]
            rounded-xl p-5
            text-center text-[13px] text-[#a8a8c0]
          ">
            No team members added yet
          </div>
        )}
        </div>
      </CompletableSection>

      {/* Add Team Member (action form - no completion button) */}
      <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-5">
        <h3 className="text-[13px] font-medium text-[#e8e8f0] mb-4">
          Add Team Member
        </h3>
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Search researcher by name..."
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
          <button
            className="
              w-full px-4 py-2
              bg-[#12122a] border border-dashed border-[#2a2a44]
              rounded-lg
              text-[13px] font-medium text-[#a8a8c0]
              hover:border-[#3a3a54] hover:text-[#e8e8f0]
              transition-colors
            "
          >
            + Add Team Member
          </button>
        </div>
      </div>

      {/* Team Assessment Section */}
      <CompletableSection
        sectionId="tm-assessment"
        title="Team Assessment"
        isComplete={isComplete('tm-assessment')}
        completedByName={getInfo('tm-assessment')?.completed_by_name}
        completedAt={getInfo('tm-assessment')?.completed_at}
        onToggle={() => toggle('tm-assessment')}
      >
        <div className="text-[13px] text-[#a8a8c0]">
          Assessment of 6 dimensions (Spin-off Intent, Founder Readiness,
          Competency Coverage, Availability, Key Person Risk, Team Dynamics)
          would appear here.
        </div>
      </CompletableSection>

      {/* Team Assessment Summary */}
      <CompletableSection
        sectionId="tm-assessment-summary"
        title="Team Assessment Summary"
        isComplete={isComplete('tm-assessment-summary')}
        completedByName={getInfo('tm-assessment-summary')?.completed_by_name}
        completedAt={getInfo('tm-assessment-summary')?.completed_at}
        onToggle={() => toggle('tm-assessment-summary')}
      >
        <div className="mb-4">
          <div className="text-[13px] text-[#a8a8c0]">
            Progress: 0/6 assessed
          </div>
        </div>
        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Overall Team Verdict
          </label>
          <InlineNote
            value={''}
            onChange={(value) => onFieldChange('team_verdict', value)}
            placeholder="Summary assessment of team readiness for spin-off"
            minHeight={100}
          />
        </div>
      </CompletableSection>
    </div>
  )
}

export default ProjectTeamTab
